/**
 * ProfileReseauSection — Lot 1A
 * Section additive intégrée à la page Profil (cockpit) pour exposer les champs
 * "Profil Réseau enrichi" : title pro, bio longue, typologies, langues,
 * expériences, zones structurées, et le panneau Visibilité (vues, abonnés,
 * complétude). Aucune modification du composant ProfilePage existant ;
 * l'intégration se fait par simple import en bas de page.
 */
import React, { useEffect, useState } from 'react';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { toast } from 'sonner';
import {
    Eye, Users, BarChart3, Briefcase, Globe, MapPin, Sparkles,
    Plus, Trash2, Loader2, ExternalLink, CheckCircle
} from 'lucide-react';
import BioAIAssistant from '../../components/reseau/BioAIAssistant';

const LANG_OPTIONS = ['fr', 'en', 'es', 'it', 'de', 'pt', 'nl'];
const TYPOLOGIE_OPTIONS = ['T1', 'T2', 'T3', 'T4', 'T5+', 'Maison', 'Maison familiale', 'Loft', 'Studio', 'Terrain', 'Local commercial'];

function slugify(name) {
    return (name || '').toLowerCase()
        .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

export default function ProfileReseauSection({ user, readOnly = false }) {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [insights, setInsights] = useState(null);
    const [form, setForm] = useState({
        title_pro: '',
        bio_long: '',
        languages: ['fr'],
        typologies: [],
        professional_zones: [],
        experience_records: [],
        started_real_estate_at: '',
    });

    useEffect(() => {
        load();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const load = async () => {
        try {
            const [previewRes, insightsRes] = await Promise.allSettled([
                api.get('/profiles/me/preview'),
                api.get('/profiles/me/insights'),
            ]);
            if (previewRes.status === 'fulfilled') {
                const p = previewRes.value.data.preview || {};
                setForm({
                    title_pro: p.title_pro || '',
                    bio_long: p.bio_long || '',
                    languages: p.languages && p.languages.length ? p.languages : ['fr'],
                    typologies: p.typologies || [],
                    professional_zones: p.professional_zones || [],
                    experience_records: p.experience_records || [],
                    started_real_estate_at: p.started_real_estate_at || '',
                });
            }
            if (insightsRes.status === 'fulfilled') {
                setInsights(insightsRes.value.data);
            }
        } catch (e) {
            console.error('Lot 1A load error', e);
        } finally {
            setLoading(false);
        }
    };

    const save = async () => {
        setSaving(true);
        try {
            await api.put('/users/me/profile', form);
            toast.success('Profil Réseau enregistré');
            const insightsRes = await api.get('/profiles/me/insights');
            setInsights(insightsRes.data);
        } catch (e) {
            toast.error('Erreur lors de l\'enregistrement');
        } finally {
            setSaving(false);
        }
    };

    const addZone = () => setForm({
        ...form,
        professional_zones: [...form.professional_zones, { label: '', postal_code: '', type: 'city' }],
    });
    const updateZone = (idx, field, value) => {
        const zones = [...form.professional_zones];
        zones[idx] = { ...zones[idx], [field]: value };
        setForm({ ...form, professional_zones: zones });
    };
    const removeZone = (idx) => setForm({
        ...form,
        professional_zones: form.professional_zones.filter((_, i) => i !== idx),
    });

    const addExperience = () => setForm({
        ...form,
        experience_records: [...form.experience_records, {
            type: 'agency', title: '', organization: '', started_at: '', ended_at: '', description: ''
        }],
    });
    const updateExperience = (idx, field, value) => {
        const xp = [...form.experience_records];
        xp[idx] = { ...xp[idx], [field]: value };
        setForm({ ...form, experience_records: xp });
    };
    const removeExperience = (idx) => setForm({
        ...form,
        experience_records: form.experience_records.filter((_, i) => i !== idx),
    });

    const toggleLang = (lang) => {
        const cur = form.languages || [];
        setForm({ ...form, languages: cur.includes(lang) ? cur.filter(l => l !== lang) : [...cur, lang] });
    };
    const toggleTypologie = (t) => {
        const cur = form.typologies || [];
        setForm({ ...form, typologies: cur.includes(t) ? cur.filter(x => x !== t) : [...cur, t] });
    };

    const publicSlug = slugify(`${user?.first_name || ''} ${user?.last_name || ''}`);

    if (loading) {
        return (
            <div className="mt-8 p-6 rounded-xl bg-white/5 border border-white/10" data-testid="profile-reseau-section-loading">
                <Loader2 className="w-5 h-5 animate-spin text-white/40" />
            </div>
        );
    }

    return (
        <div className="mt-12 space-y-8" data-testid="profile-reseau-section">
            {/* En-tête Réseau Homly */}
            <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-gradient-to-br from-purple-500/20 to-orange-500/20 border border-purple-500/30">
                        <Sparkles className="w-5 h-5 text-purple-300" />
                    </div>
                    <div>
                        <h2 className="text-xl font-semibold text-white">Profil Réseau Homly</h2>
                        <p className="text-sm text-white/50">Ce que les visiteurs voient sur votre page publique</p>
                    </div>
                </div>
                <a
                    href={`/p/agent/${publicSlug}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="preview-public-profile-btn"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-purple-600 to-orange-500 text-white text-sm font-medium hover:opacity-90"
                >
                    <ExternalLink className="w-4 h-4" />
                    Voir mon profil public
                </a>
            </div>

            {/* Panneau Insights (Visibilité) */}
            {insights && (
                <div
                    className="grid grid-cols-2 md:grid-cols-4 gap-3"
                    data-testid="insights-grid"
                >
                    <InsightCard
                        icon={Eye}
                        label="Vues 30 jours"
                        value={insights.views_30d}
                        testId="insight-views-30d"
                    />
                    <InsightCard
                        icon={Eye}
                        label="Vues 7 jours"
                        value={insights.views_7d}
                        testId="insight-views-7d"
                    />
                    <InsightCard
                        icon={Users}
                        label="Abonnés"
                        value={insights.followers_count}
                        sub={`+${insights.new_followers_30d} ce mois`}
                        testId="insight-followers"
                    />
                    <InsightCard
                        icon={BarChart3}
                        label="Complétude"
                        value={`${insights.completeness?.score_percent || 0}%`}
                        testId="insight-completeness"
                    />
                </div>
            )}

            {insights?.completeness?.missing_fields?.length > 0 && (
                <div
                    className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/20"
                    data-testid="completeness-hint"
                >
                    <div className="flex items-center justify-between gap-3">
                        <div className="flex-1">
                            <div className="text-sm text-amber-200 font-medium">
                                Complétez votre profil pour gagner en visibilité
                            </div>
                            <Progress value={insights.completeness.score_percent} className="mt-2 h-1.5" />
                        </div>
                        <div className="text-xs text-white/60">
                            Manque : {insights.completeness.missing_fields.slice(0, 3).join(', ')}
                            {insights.completeness.missing_fields.length > 3 ? '…' : ''}
                        </div>
                    </div>
                </div>
            )}

            {/* Formulaire édition — masqué en mode lecture seule */}
            {!readOnly && (
            <div className="space-y-6 p-6 rounded-xl bg-white/[0.02] border border-white/10">
                <div className="space-y-2">
                    <Label className="text-white/70 flex items-center gap-2">
                        <Briefcase className="w-4 h-4" /> Titre professionnel
                    </Label>
                    <Input
                        value={form.title_pro}
                        onChange={(e) => setForm({ ...form, title_pro: e.target.value })}
                        placeholder="Ex : Conseillère immobilière indépendante"
                        data-testid="input-title-pro"
                        className="bg-white/5 border-white/10 text-white"
                    />
                </div>

                <div className="space-y-2">
                    <div className="flex items-center justify-between gap-2">
                        <Label className="text-white/70">Bio longue (présentation détaillée)</Label>
                        <BioAIAssistant
                            currentBio={form.bio_long}
                            onApply={(text) => setForm({ ...form, bio_long: text })}
                        />
                    </div>
                    <Textarea
                        rows={4}
                        value={form.bio_long}
                        onChange={(e) => setForm({ ...form, bio_long: e.target.value })}
                        placeholder="Décrivez votre approche, votre expérience, vos zones de prédilection..."
                        data-testid="input-bio-long"
                        className="bg-white/5 border-white/10 text-white"
                    />
                </div>

                <div className="space-y-2">
                    <Label className="text-white/70 flex items-center gap-2">
                        <Globe className="w-4 h-4" /> Langues parlées
                    </Label>
                    <div className="flex flex-wrap gap-2" data-testid="languages-chips">
                        {LANG_OPTIONS.map(l => (
                            <button
                                key={l}
                                type="button"
                                onClick={() => toggleLang(l)}
                                data-testid={`lang-chip-${l}`}
                                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${
                                    form.languages?.includes(l)
                                        ? 'bg-purple-500/20 border-purple-500/50 text-purple-200'
                                        : 'bg-white/5 border-white/10 text-white/60 hover:bg-white/10'
                                }`}
                            >
                                {l.toUpperCase()}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="space-y-2">
                    <Label className="text-white/70">Typologies de biens</Label>
                    <div className="flex flex-wrap gap-2" data-testid="typologies-chips">
                        {TYPOLOGIE_OPTIONS.map(t => (
                            <button
                                key={t}
                                type="button"
                                onClick={() => toggleTypologie(t)}
                                data-testid={`typologie-chip-${t}`}
                                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${
                                    form.typologies?.includes(t)
                                        ? 'bg-orange-500/20 border-orange-500/50 text-orange-200'
                                        : 'bg-white/5 border-white/10 text-white/60 hover:bg-white/10'
                                }`}
                            >
                                {t}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="space-y-3">
                    <div className="flex items-center justify-between">
                        <Label className="text-white/70 flex items-center gap-2">
                            <MapPin className="w-4 h-4" /> Zones d&apos;intervention
                        </Label>
                        <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            onClick={addZone}
                            data-testid="add-zone-btn"
                            className="text-purple-300 hover:bg-purple-500/10"
                        >
                            <Plus className="w-4 h-4 mr-1" /> Ajouter
                        </Button>
                    </div>
                    {form.professional_zones.map((z, idx) => (
                        <div key={idx} className="flex gap-2" data-testid={`zone-row-${idx}`}>
                            <Input
                                value={z.label || ''}
                                onChange={(e) => updateZone(idx, 'label', e.target.value)}
                                placeholder="Ville / quartier"
                                data-testid={`zone-label-${idx}`}
                                className="bg-white/5 border-white/10 text-white flex-1"
                            />
                            <Input
                                value={z.postal_code || ''}
                                onChange={(e) => updateZone(idx, 'postal_code', e.target.value)}
                                placeholder="Code postal"
                                data-testid={`zone-postal-${idx}`}
                                className="bg-white/5 border-white/10 text-white w-40"
                            />
                            <Button
                                type="button"
                                size="icon"
                                variant="ghost"
                                onClick={() => removeZone(idx)}
                                data-testid={`zone-remove-${idx}`}
                                className="text-red-300 hover:bg-red-500/10"
                            >
                                <Trash2 className="w-4 h-4" />
                            </Button>
                        </div>
                    ))}
                    {form.professional_zones.length === 0 && (
                        <div className="text-xs text-white/40 italic">Aucune zone configurée. Cliquez sur Ajouter.</div>
                    )}
                </div>

                <div className="space-y-3">
                    <div className="flex items-center justify-between">
                        <Label className="text-white/70 flex items-center gap-2">
                            <Briefcase className="w-4 h-4" /> Expériences
                        </Label>
                        <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            onClick={addExperience}
                            data-testid="add-experience-btn"
                            className="text-purple-300 hover:bg-purple-500/10"
                        >
                            <Plus className="w-4 h-4 mr-1" /> Ajouter
                        </Button>
                    </div>
                    {form.experience_records.map((xp, idx) => (
                        <div key={idx} className="p-3 rounded-lg bg-white/[0.02] border border-white/5 space-y-2" data-testid={`experience-row-${idx}`}>
                            <div className="flex gap-2">
                                <Input
                                    value={xp.title || ''}
                                    onChange={(e) => updateExperience(idx, 'title', e.target.value)}
                                    placeholder="Intitulé du poste"
                                    data-testid={`xp-title-${idx}`}
                                    className="bg-white/5 border-white/10 text-white flex-1"
                                />
                                <Input
                                    value={xp.organization || ''}
                                    onChange={(e) => updateExperience(idx, 'organization', e.target.value)}
                                    placeholder="Organisation"
                                    data-testid={`xp-organization-${idx}`}
                                    className="bg-white/5 border-white/10 text-white flex-1"
                                />
                                <Button
                                    type="button"
                                    size="icon"
                                    variant="ghost"
                                    onClick={() => removeExperience(idx)}
                                    data-testid={`xp-remove-${idx}`}
                                    className="text-red-300 hover:bg-red-500/10"
                                >
                                    <Trash2 className="w-4 h-4" />
                                </Button>
                            </div>
                            <div className="flex gap-2">
                                <Input
                                    type="date"
                                    value={xp.started_at || ''}
                                    onChange={(e) => updateExperience(idx, 'started_at', e.target.value)}
                                    data-testid={`xp-start-${idx}`}
                                    className="bg-white/5 border-white/10 text-white flex-1"
                                />
                                <Input
                                    type="date"
                                    value={xp.ended_at || ''}
                                    onChange={(e) => updateExperience(idx, 'ended_at', e.target.value)}
                                    data-testid={`xp-end-${idx}`}
                                    className="bg-white/5 border-white/10 text-white flex-1"
                                    placeholder="vide = présent"
                                />
                            </div>
                            <Textarea
                                rows={2}
                                value={xp.description || ''}
                                onChange={(e) => updateExperience(idx, 'description', e.target.value)}
                                placeholder="Description courte"
                                data-testid={`xp-description-${idx}`}
                                className="bg-white/5 border-white/10 text-white"
                            />
                        </div>
                    ))}
                    {form.experience_records.length === 0 && (
                        <div className="text-xs text-white/40 italic">Aucune expérience renseignée.</div>
                    )}
                </div>

                <div className="flex justify-end pt-2">
                    <Button
                        type="button"
                        onClick={save}
                        disabled={saving}
                        data-testid="save-reseau-btn"
                        className="bg-gradient-to-r from-purple-600 to-orange-500 text-white"
                    >
                        {saving ? (
                            <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Enregistrement…
                            </>
                        ) : (
                            <>
                                <CheckCircle className="w-4 h-4 mr-2" /> Enregistrer mon profil réseau
                            </>
                        )}
                    </Button>
                </div>
            </div>
            )}
        </div>
    );
}

function InsightCard({ icon: Icon, label, value, sub, testId }) {
    return (
        <div className="p-4 rounded-xl bg-white/[0.03] border border-white/10" data-testid={testId}>
            <div className="flex items-center gap-2 text-white/50 text-xs uppercase tracking-wider">
                <Icon className="w-3.5 h-3.5" /> {label}
            </div>
            <div className="mt-2 text-2xl font-semibold text-white" data-testid={`${testId}-value`}>{value}</div>
            {sub && <div className="text-xs text-white/40 mt-0.5">{sub}</div>}
        </div>
    );
}
